import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { Container } from '@carouselgg/nexus/container'
import { Text } from '@carouselgg/nexus/text'
import { Button } from '@carouselgg/nexus/button'
import { Badge } from '@carouselgg/nexus/badge'
import { ConfirmDialog } from '@carouselgg/nexus/confirm-dialog'
import {
  ArrowLeftRight,
  Link2,
  ArrowLeft, Trash2, Bell,
  Calendar, CalendarClock, CalendarX, Trophy,
  UserPlus, UserMinus, Megaphone, CreditCard,
  ScrollText, PlayCircle, Clock, FlagTriangleLeft,
  GraduationCap, Binoculars,
  ExternalLink, UserCheck, UserX, ClipboardList,
  CreditCard as Pay, BookOpen, Search, Crown, Hand, Users,
  MessageSquare, ContactRound, UserRoundX,
  CircleCheck,
  CircleX,
  Gavel,
  Inbox,
  Sparkles,
  MessageSquareReply,
  Undo2,
  DoorOpen,
} from '@carouselgg/nexus/icon'
import { relativeTime } from '@carouselgg/nexus/notification-center'
import type { NotificationType } from '@carouselgg/nexus/notification-center'
import { useAppNotifications } from '../context/NotificationContext'
import { TeamInviteAnswer } from '../components/TeamInviteAnswer'
import { SideSwapAnswer } from '../components/SideSwapAnswer'
import styles from './NotificationDetailPage.module.css'

// Keyed by string: the team_contact_* kinds are newer than the shared NotificationType union.
const typeIcons: Record<string, typeof Calendar> = {
  match_scheduled: Calendar,
  match_rescheduled: CalendarClock,
  match_cancelled: CalendarX,
  match_result: Trophy,
  team_invite: UserPlus,
  roster_player_added: UserPlus,
  roster_player_removed: UserMinus,
  season_registration_open: PlayCircle,
  season_closing_soon: Clock,
  season_started: FlagTriangleLeft,
  season_ended: FlagTriangleLeft,
  org_announcement: Megaphone,
  league_rule_update: ScrollText,
  payment_reminder: CreditCard,
  eligibility_change: GraduationCap,
  scouting_interest: Binoculars,
  org_invite: UserPlus,
  org_member_joined: UserPlus,
  guest_team_claimable: UserCheck,
  guest_team_ownable: Crown,
  guest_team_owner_requested: Hand,
  guest_team_owner_decided: Crown,
  team_contact_primary: ContactRound,
  team_contact_changed: ContactRound,
  team_contact_lost: UserRoundX,
  team_contact_event: ContactRound,
  team_contact_event_changed: ContactRound,
  team_contact_method_requested: ContactRound,
  team_contact_message: MessageSquare,
  team_contact_league_request: ContactRound,
  competition_entry_received: Inbox,
  competition_entry_approved: CircleCheck,
  competition_entry_rejected: CircleX,
  competition_entry_withdrawn: Undo2,
  competition_entry_leave_requested: DoorOpen,
  competition_ruling: Gavel,
  match_sides: ArrowLeftRight,
  side_swap_request: ArrowLeftRight,
  riot_link_request: Link2,
  league_roster_change: UserPlus,
  free_agent_request_received: UserPlus,
  free_agent_admitted: UserCheck,
  free_agent_rejected: UserRoundX,
  free_agent_recommended: Sparkles,
  free_agent_recommendation_answered: MessageSquareReply,
}

const typeLabels: Record<string, string> = {
  match_scheduled: 'Match Scheduled',
  match_rescheduled: 'Match Rescheduled',
  match_cancelled: 'Match Cancelled',
  match_result: 'Match Result',
  team_invite: 'Team Invite',
  roster_player_added: 'Roster Update',
  roster_player_removed: 'Roster Update',
  season_registration_open: 'Season',
  season_closing_soon: 'Season',
  season_started: 'Season',
  season_ended: 'Season',
  org_announcement: 'Announcement',
  league_rule_update: 'Rule Update',
  payment_reminder: 'Payment',
  eligibility_change: 'Eligibility',
  scouting_interest: 'Scouting',
  org_invite: 'Organization Invite',
  org_member_joined: 'Member Joined',
  guest_team_claimable: 'Team Can Be Handed Over',
  guest_team_ownable: 'Team Has No Owner',
  guest_team_owner_requested: 'Ownership Request',
  guest_team_owner_decided: 'Ownership Decision',
  team_contact_primary: 'Team Contact',
  team_contact_changed: 'Team Contact',
  team_contact_lost: 'Team Contact',
  team_contact_event: 'Event Contact',
  team_contact_event_changed: 'Event Contact',
  team_contact_method_requested: 'Contact Method',
  team_contact_message: 'Message',
  team_contact_league_request: 'Contact Needed',
  competition_entry_received: 'Entry to Review',
  competition_entry_approved: 'Entry Approved',
  competition_entry_rejected: 'Entry Not Accepted',
  competition_entry_withdrawn: 'Entry Withdrawn',
  competition_entry_leave_requested: 'Asked to Leave',
  competition_ruling: 'League Ruling',
  match_sides: 'Starting Sides',
  side_swap_request: 'Side Swap',
  riot_link_request: 'Link Riot',
  league_roster_change: 'Roster Change',
  free_agent_request_received: 'Free Agent to Review',
  free_agent_admitted: 'Free Agent Approved',
  free_agent_rejected: 'Free Agent Decision',
  free_agent_recommended: 'Free Agent Recommended',
  free_agent_recommendation_answered: 'Captain Answered',
}

const orgTypeBadgeVariant = {
  league: 'info',
  organization: 'default',
  university: 'success',
} as const

const contactActionLabels: Record<string, string> = {
  team_contact_message: 'Open conversation',
  team_contact_method_requested: 'Add a contact method',
  team_contact_league_request: 'Choose a contact',
  team_contact_lost: 'Choose a contact',
}

export function NotificationActions({ type, href, onNavigate }: { type: NotificationType; href?: string; onNavigate: (path: string) => void }) {
  const go = (path?: string) => { if (path) onNavigate(path) }
  const kind: string = type

  if (kind in contactActionLabels) {
    return href ? (
      <Button variant="primary" size="sm" onClick={() => go(href)}>
        {kind === 'team_contact_message' ? <MessageSquare size={14} /> : <ContactRound size={14} />}
        {contactActionLabels[kind]}
      </Button>
    ) : null
  }

  switch (type) {
    case 'match_scheduled':
    case 'match_rescheduled':
      return (
        <>
          <Button variant="primary" size="sm" onClick={() => go(href)}>
            <CalendarClock size={14} />
            View Schedule
          </Button>
        </>
      )
    case 'match_cancelled':
      return (
        <Button variant="secondary" size="sm" onClick={() => go(href)}>
          <CalendarX size={14} />
          View Schedule
        </Button>
      )
    case 'match_result':
      return (
        <>
          <Button variant="primary" size="sm" onClick={() => go(href)}>
            <Trophy size={14} />
            View Standings
          </Button>
        </>
      )
    case 'team_invite':
      return (
        <>
          <Button variant="primary" size="sm" onClick={() => go(href)}>
            <UserCheck size={14} />
            Accept Invite
          </Button>
          <Button variant="ghost" size="sm" onClick={() => go(href)}>
            <UserX size={14} />
            Decline
          </Button>
        </>
      )
    case 'roster_player_added':
    case 'roster_player_removed':
      return (
        <Button variant="secondary" size="sm" onClick={() => go(href)}>
          <ClipboardList size={14} />
          View Roster
        </Button>
      )
    case 'season_registration_open':
    case 'season_closing_soon':
      return (
        <Button variant="primary" size="sm" onClick={() => go(href)}>
          <PlayCircle size={14} />
          Register Now
        </Button>
      )
    case 'season_started':
    case 'season_ended':
      return (
        <Button variant="secondary" size="sm" onClick={() => go(href)}>
          <ExternalLink size={14} />
          View Season
        </Button>
      )
    case 'org_invite':
      return (
        <>
          <Button variant="primary" size="sm" onClick={() => go(href)}>
            <UserCheck size={14} />
            Accept Invite
          </Button>
          <Button variant="ghost" size="sm" onClick={() => go(href)}>
            <UserX size={14} />
            Decline
          </Button>
        </>
      )
    case 'org_member_joined':
    case 'org_announcement':
    case 'league_rule_update':
      return (
        <Button variant="secondary" size="sm" onClick={() => go(href)}>
          <BookOpen size={14} />
          {type === 'league_rule_update' ? 'View Rules' : 'Read More'}
        </Button>
      )
    case 'payment_reminder':
      return (
        <Button variant="primary" size="sm" onClick={() => go(href)}>
          <Pay size={14} />
          Pay Now
        </Button>
      )
    case 'eligibility_change':
      return (
        <Button variant="secondary" size="sm" onClick={() => go(href)}>
          <GraduationCap size={14} />
          View Eligibility
        </Button>
      )
    case 'scouting_interest':
      return (
        <Button variant="primary" size="sm" onClick={() => go(href)}>
          <Search size={14} />
          View Profile Interest
        </Button>
      )
    // The team's page is where the player asks; the banner there does the asking.
    case 'guest_team_ownable':
      return (
        <Button variant="primary" size="sm" onClick={() => go(href)}>
          <Crown size={14} />
          Ask to own
        </Button>
      )
    case 'guest_team_claimable':
    case 'guest_team_owner_requested':
      return (
        <Button variant="primary" size="sm" onClick={() => go(href)}>
          <Users size={14} />
          Open roster
        </Button>
      )
    case 'guest_team_owner_decided':
      return (
        <Button variant="secondary" size="sm" onClick={() => go(href)}>
          <ExternalLink size={14} />
          View team
        </Button>
      )
    case 'riot_link_request':
      return (
        <Button variant="primary" size="sm" onClick={() => go(href ?? '/settings/connections')}>
          <Link2 size={14} />
          Link Riot account
        </Button>
      )
    default:
      return href ? (
        <Button variant="secondary" size="sm" onClick={() => go(href)}>
          <ExternalLink size={14} />
          View Details
        </Button>
      ) : null
  }
}

export function NotificationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { getById, markRead, dismiss } = useAppNotifications()

  const notification = id ? getById(id) : undefined
  const [showConfirm, setShowConfirm] = useState(false)

  // Auto-mark as read when viewing
  useEffect(() => {
    if (notification && !notification.read) {
      markRead(notification.id)
    }
  }, [notification, markRead])

  if (!notification) {
    return (
      <Container size="sm">
        <div className={styles.notFound}>
          <Text as="h1" size="xl" weight="bold">Notification not found</Text>
          <Text as="p" muted>This notification may have been removed.</Text>
          <Button variant="secondary" size="sm" onClick={() => navigate('/notifications')}>
            <ArrowLeft size={14} />
            Back to Notifications
          </Button>
        </div>
      </Container>
    )
  }

  const Icon = typeIcons[notification.type] ?? Bell

  const handleDelete = () => {
    dismiss(notification.id)
    navigate('/notifications')
  }

  return (
    <Container size="md">
      <div className={styles.page}>
        {/* Back link */}
        <div className={styles.topBar}>
          <Link to="/notifications" className={styles.backLink}>
            <ArrowLeft size={14} />
            <span>Notifications</span>
          </Link>
          <Button variant="ghost" size="sm" onClick={() => setShowConfirm(true)}>
            <Trash2 size={14} />
            Delete
          </Button>
        </div>

        {/* Card */}
        <div className={styles.card}>
          {/* Type + time header */}
          <div className={styles.meta}>
            <div className={styles.typeBadge}>
              <Icon size={14} />
              <span>{typeLabels[notification.type] ?? 'Notification'}</span>
            </div>
            <span className={styles.time}>{relativeTime(notification.createdAt)}</span>
          </div>

          {/* Title */}
          <Text as="h1" size="xl" weight="bold" className={styles.title}>
            {notification.title}
          </Text>

          {/* Org info */}
          <div className={styles.org}>
            {notification.org.logoUrl ? (
              <img src={notification.org.logoUrl} alt={notification.org.name} className={styles.orgLogo} />
            ) : (
              <span className={styles.orgFallback}>{notification.org.name.charAt(0)}</span>
            )}
            <Link to={`/org/${notification.org.slug}`} className={styles.orgName}>
              {notification.org.name}
            </Link>
            <Badge variant={orgTypeBadgeVariant[notification.org.type]} size="sm">
              {notification.org.type}
            </Badge>
          </div>

          {/* Body */}
          <div className={styles.body}>
            <Text as="p" size="base">{notification.body}</Text>
          </div>

          {/* Contextual actions */}
          <div className={styles.actions}>
            {notification.type === 'team_invite' && notification.meta?.application_id
              ? <TeamInviteAnswer applicationId={notification.meta.application_id} />
              : notification.type === 'side_swap_request' && notification.meta?.swap_id
                ? <SideSwapAnswer orgId={notification.org.id} swapId={notification.meta.swap_id} />
                : <NotificationActions type={notification.type} href={notification.href} onNavigate={navigate} />}
          </div>
        </div>

        {/* Timestamp */}
        <Text as="p" size="xs" muted className={styles.timestamp}>
          {new Date(notification.createdAt).toLocaleString(undefined, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </Text>
      </div>
      <ConfirmDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleDelete}
        title="Delete notification?"
        description={`This notification from ${notification.org.name} will be removed.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </Container>
  )
}
